import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Send, Wind, User, Users, CircleDot, Check, Plus, Pencil, Trash2 } from "lucide-react";
import { WeeklyPlanEditorDialog } from "@/pages/coach/plans/WeeklyPlanEditorDialog";

const STATUS_CLS = {
  accepted: "bg-emerald-100 text-emerald-700",
  declined: "bg-rose-100 text-rose-700",
  active: "bg-amber-100 text-amber-700",
};

export default function WeeklyPlansTab() {
  const t = useT();
  const [plans, setPlans] = useState(null);
  const [clients, setClients] = useState([]);
  const [circles, setCircles] = useState([]);
  const [share, setShare] = useState(null); // plan being shared
  const [editor, setEditor] = useState(null); // { planId } | { new: true }

  const load = useCallback(async () => {
    try {
      const [p, cl, cr] = await Promise.all([
        api.get("/breathwork/plans/coach/mine").then((r) => r.data).catch(() => []),
        api.get("/coach/plan-clients").then((r) => r.data).catch(() => []),
        api.get("/coach/circles/local-list").then((r) => r.data?.circles || []).catch(() => []),
      ]);
      setPlans(Array.isArray(p) ? p : []);
      setClients(Array.isArray(cl) ? cl : []);
      setCircles(Array.isArray(cr) ? cr : []);
    } catch { setPlans([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const removePlan = async (p) => {
    if (!window.confirm(t("pl.w_delete_confirm", { name: p.name }))) return;
    try { await api.delete(`/breathwork/plans/${p.id}`); toast.success(t("pl.w_deleted")); await load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const mineOnly = (plans || []).filter((p) => p.assigned_to === p.owner_id);
  const shared = (plans || []).filter((p) => p.assigned_to !== p.owner_id);

  return (
    <div data-testid="pl-weekly-tab">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{t("pl.w_web_note")}</p>
        <Button className="gap-1.5 rounded-full" onClick={() => setEditor({ new: true })} data-testid="pl-weekly-new"><Plus className="h-4 w-4" /> {t("pl.w_new")}</Button>
      </div>

      {plans === null ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>
      ) : (
        <>
          <p className="mb-2 font-display text-lg text-foreground">{t("pl.w_my_master")}</p>
          {mineOnly.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><Wind className="h-6 w-6" /></div>
              <p className="mt-3 text-sm text-muted-foreground">{t("pl.w_no_plans")}</p>
              <Button className="mt-4 gap-1.5 rounded-full" onClick={() => setEditor({ new: true })} data-testid="pl-weekly-empty-cta"><Plus className="h-4 w-4" /> {t("pl.w_new")}</Button>
            </div>
          ) : (
            <div className="space-y-2">
              {mineOnly.map((p) => (
                <div key={p.id} data-testid={`pl-weekly-${p.id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15"><Wind className="h-5 w-5 text-primary" /></div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-foreground">{p.name}</p>
                    {p.description && <p className="truncate text-xs text-muted-foreground">{p.description}</p>}
                    <p className="mt-0.5 text-[11px] text-muted-foreground">{t("pl.w_slots", { n: p.slots?.length || 0 })} · {p.week_start || t("pl.w_flexible")}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={() => setEditor({ planId: p.id })} data-testid={`pl-weekly-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full text-rose-500 hover:text-rose-600" onClick={() => removePlan(p)} data-testid={`pl-weekly-del-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                    <Button size="sm" className="gap-1.5 rounded-full" onClick={() => setShare(p)} data-testid={`pl-share-${p.id}`}><Send className="h-3.5 w-3.5" /> {t("pl.w_send")}</Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <p className="mb-2 mt-6 font-display text-lg text-foreground">{t("pl.w_sent")}</p>
          {shared.length === 0 ? (
            <p className="rounded-xl border border-dashed border-border bg-card p-3 text-sm text-muted-foreground">{t("pl.w_sent_hint")}</p>
          ) : (
            <div className="space-y-2">
              {shared.map((p) => (
                <div key={p.id} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-foreground">{p.name}</p>
                    <p className="text-[11px] text-muted-foreground">{t("pl.w_to_client")}</p>
                  </div>
                  <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase ${STATUS_CLS[p.status] || "bg-secondary text-muted-foreground"}`}>{p.status}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {share && <ShareDialog plan={share} clients={clients} circles={circles} onClose={() => { setShare(null); load(); }} />}
      {editor && <WeeklyPlanEditorDialog open planId={editor.planId || null} onClose={(saved) => { setEditor(null); if (saved) load(); }} />}
    </div>
  );
}

function ShareDialog({ plan, clients, circles, onClose }) {
  const t = useT();
  const [mode, setMode] = useState("single");
  const [single, setSingle] = useState("");
  const [multi, setMulti] = useState(new Set());
  const [circle, setCircle] = useState("");
  const [overwrite, setOverwrite] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const canSend = useMemo(() => {
    if (mode === "single") return !!single;
    if (mode === "multi") return multi.size > 0;
    return !!circle;
  }, [mode, single, multi, circle]);

  const toggleMulti = (id) => setMulti((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const doShare = async () => {
    if (!canSend) return;
    if (mode === "multi" && !window.confirm(t("rw3.pl_confirm_multi", { name: plan.name, n: multi.size }))) return;
    if (mode === "circle" && !window.confirm(t("rw3.pl_confirm_circle", { name: plan.name }))) return;
    const msg = message.trim() || undefined;
    setSending(true);
    try {
      if (mode === "single") {
        await api.post(`/breathwork/plans/${plan.id}/share`, { client_id: single, message: msg });
        toast.success(t("pl.w_sent_ok"));
      } else if (mode === "multi") {
        let ok = 0, fail = 0;
        for (const cid of Array.from(multi)) {
          try { await api.post(`/breathwork/plans/${plan.id}/share`, { client_id: cid, message: msg }); ok += 1; }
          catch { fail += 1; }
        }
        toast.success(t("pl.w_multi_summary", { ok, fail }));
      } else {
        const { data } = await api.post(`/breathwork/plans/${plan.id}/share-with-circle`, { circle_id: circle, message: msg, overwrite_existing: overwrite });
        toast.success(t("pl.w_circle_summary", { sent: data?.inserted ?? 0, skipped: data?.skipped ?? 0, updated: data?.updated ?? 0 }));
      }
      onClose();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSending(false); }
  };

  const TABS = [{ k: "single", icon: User }, { k: "multi", icon: Users }, { k: "circle", icon: CircleDot }];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="pl-share-dialog">
        <DialogHeader>
          <DialogTitle className="truncate">{t("pl.w_share_target")}</DialogTitle>
          <DialogDescription className="sr-only">{t("pl.w_share_target")}</DialogDescription>
        </DialogHeader>
        <p className="truncate font-display text-foreground">{plan.name}</p>

        <div className="flex gap-1 rounded-full bg-secondary p-1">
          {TABS.map(({ k, icon: Icon }) => (
            <button key={k} onClick={() => setMode(k)} data-testid={`pl-tab-${k}`} className={`flex flex-1 items-center justify-center gap-1.5 rounded-full py-2 text-sm font-medium transition-colors ${mode === k ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              <Icon className="h-4 w-4" /> {t(`pl.w_tab_${k}`)}
            </button>
          ))}
        </div>

        <div className="mt-1 space-y-1.5">
          {mode === "single" && (clients.length === 0 ? <Empty t={t} k="pl.w_no_clients" /> : clients.map((c) => (
            <Row key={c.id} active={single === c.id} onClick={() => setSingle(c.id)} testID={`pl-single-${c.id}`} label={c.name || c.email} radio />
          )))}
          {mode === "multi" && (
            <>
              <div className="mb-1 flex justify-end gap-2">
                <button onClick={() => setMulti(new Set(clients.map((c) => c.id)))} className="text-xs font-semibold text-primary" data-testid="pl-select-all">{t("pl.w_select_all")}</button>
                <button onClick={() => setMulti(new Set())} className="text-xs font-semibold text-primary" data-testid="pl-deselect-all">{t("pl.w_deselect_all")}</button>
              </div>
              {clients.length === 0 ? <Empty t={t} k="pl.w_no_clients" /> : clients.map((c) => (
                <Row key={c.id} active={multi.has(c.id)} onClick={() => toggleMulti(c.id)} testID={`pl-multi-${c.id}`} label={c.name || c.email} />
              ))}
            </>
          )}
          {mode === "circle" && (
            <>
              {circles.length === 0 ? <Empty t={t} k="pl.w_no_circles" /> : circles.map((c) => (
                <Row key={c.id} active={circle === c.id} onClick={() => setCircle(c.id)} testID={`pl-circle-${c.id}`} label={c.name} sub={t("pl.w_circle_members", { n: c.member_count ?? 0 })} radio />
              ))}
              <label className="mt-2 flex items-center justify-between rounded-xl border border-border p-3">
                <span className="text-sm text-foreground">{t("pl.w_overwrite")}</span>
                <Switch checked={overwrite} onCheckedChange={setOverwrite} data-testid="pl-overwrite" />
              </label>
            </>
          )}
        </div>

        <Textarea className="mt-2 min-h-[64px]" value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t("pl.w_msg_ph")} data-testid="pl-share-msg" />
        <Button className="mt-1 w-full gap-1.5 rounded-full" disabled={!canSend || sending} onClick={doShare} data-testid="pl-do-share"><Send className="h-4 w-4" /> {sending ? t("pl.w_sending") : t("pl.w_send_plan")}</Button>
      </DialogContent>
    </Dialog>
  );
}

function Row({ active, onClick, testID, label, sub, radio }) {
  return (
    <button onClick={onClick} data-testid={testID} className={`flex w-full items-center gap-2 rounded-xl border p-2.5 text-left ${active ? "border-primary bg-primary/10" : "border-border hover:bg-accent"}`}>
      <span className={`flex h-5 w-5 shrink-0 items-center justify-center border-2 ${radio ? "rounded-full" : "rounded"} ${active ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground"}`}>{active && <Check className="h-3 w-3" />}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm text-foreground">{label}</span>
        {sub && <span className="block text-[11px] text-muted-foreground">{sub}</span>}
      </span>
    </button>
  );
}

function Empty({ t, k }) {
  return <p className="rounded-xl border border-dashed border-border bg-card p-3 text-sm text-muted-foreground">{t(k)}</p>;
}
